import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../../styling/stylingHelper';
import { fmtBase } from '../../../utils/reports/shared';
import { WasteByProductResult, WasteByProductRow, WASTE_REASON_LABELS } from '../../../utils/reports/wasteByProduct';
import { ResponsiveTable, MobileCardField } from '../../shared/mobile/ResponsiveTable';

interface Props {
  data: WasteByProductResult;
  /** Entries in the period, so an empty table can say WHY it is empty. */
  periodEntryCount: number;
}

/**
 * "Waste by product": the period's reported waste rolled up per canonical product key —
 * linked dish entries exploded through their recipe, plus linked ingredient entries. This
 * is the dataset Phase 2 consumes, so the table shows the split (how much came from dishes
 * vs. from directly logged ingredients) rather than one opaque total.
 */
export default function WasteByProductTable({ data, periodEntryCount }: Props) {
  const { t } = useTranslation();

  if (data.rows.length === 0) {
    return (
      <div className="text-center py-12">
        <p className={cn('text-base', tertiaryText)}>
          {periodEntryCount === 0 ? t('waste.noEntries') : t('waste.byProduct.empty')}
        </p>
      </div>
    );
  }

  const reasonsLabel = (row: WasteByProductRow): string =>
    row.reasons
      .map((r) => `${r.reason ? t(WASTE_REASON_LABELS[r.reason] || r.reason) : t('waste.byProduct.reasonUnset')} (${r.entries})`)
      .join(', ');

  return (
    <div className="space-y-4">
      {/* Flagged contributions — held out of the totals rather than silently summed. */}
      {data.flags.length > 0 && (
        <div className="p-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <p className={cn(smallTextSize, semiBoldText, 'text-amber-800 dark:text-amber-200 flex items-center gap-2')}>
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {t('waste.byProduct.flaggedTitle', { n: data.flags.length })}
          </p>
          <ul className="mt-2 space-y-1">
            {data.flags.map((f, i) => (
              <li key={`${f.wasteId}-${i}`} className="text-[12px] text-amber-800 dark:text-amber-200">
                <span className={semiBoldText}>{f.itemName}</span>
                {' — '}
                {f.kind === 'unit_mismatch'
                  ? t('waste.byProduct.flagUnitMismatch', {
                      entryUnit: f.entryUnit,
                      productUnit: f.productUnit,
                      product: f.productName,
                    })
                  : f.kind === 'dish_no_match'
                    ? t('waste.byProduct.flagDishNoMatch')
                    : t('waste.byProduct.flagDishNoProducts')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Desktop table (≥sm) + mobile card list (<sm) — gates live inside ResponsiveTable. */}
      <ResponsiveTable
        mobile={
          <div className="flex flex-col gap-3">
            {data.rows.map((row) => (
          <div key={row.key} className={cn(cardClass, borderPrimary, 'border rounded-xl p-4')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className={cn('block', smallTextSize, semiBoldText, primaryText, 'truncate')}>{row.productName}</span>
                <span className={cn('block text-[11px]', tertiaryText, 'truncate')}>
                  {[row.platform, row.articleId].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="text-right shrink-0">
                <span className={cn(smallTextSize, semiBoldText, primaryText, 'whitespace-nowrap')}>
                  {fmtBase(row.totalQuantity, row.stdUnit)}
                </span>
                {row.mismatchedEntries > 0 && (
                  <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                    {t('waste.byProduct.mismatchBadge', { n: row.mismatchedEntries })}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-y-2">
              <MobileCardField label={t('waste.byProduct.colEntries')} value={row.entryCount} divider />
              {(row.fromDishQuantity > 0 || row.fromIngredientQuantity > 0) && (
                <MobileCardField
                  label={t('waste.byProduct.colSplit')}
                  value={
                    <span className="flex flex-col">
                      {row.fromDishQuantity > 0 && (
                        <span>
                          {t('waste.byProduct.fromDish', {
                            qty: fmtBase(row.fromDishQuantity, row.stdUnit),
                            n: row.fromDishEntries,
                          })}
                        </span>
                      )}
                      {row.fromIngredientQuantity > 0 && (
                        <span>
                          {t('waste.byProduct.fromIngredient', {
                            qty: fmtBase(row.fromIngredientQuantity, row.stdUnit),
                            n: row.fromIngredientEntries,
                          })}
                        </span>
                      )}
                    </span>
                  }
                />
              )}
            </div>

            <div className="mt-3">
              <span className={cn('block text-xs font-medium leading-[18px]', tertiaryText)}>
                {t('waste.byProduct.colReasons')}
              </span>
              <span className={cn('block', smallTextSize, secondaryText)}>{reasonsLabel(row)}</span>
            </div>
          </div>
            ))}
          </div>
        }
        desktop={
          <div className={cn(cardClass, borderPrimary, 'border rounded-xl overflow-x-auto')}>
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className={cn('border-b', borderPrimary)}>
              <th className={cn('px-4 py-3', smallTextSize, semiBoldText, secondaryText)}>{t('waste.byProduct.colProduct')}</th>
              <th className={cn('px-4 py-3 text-right', smallTextSize, semiBoldText, secondaryText)}>{t('waste.byProduct.colQuantity')}</th>
              <th className={cn('px-4 py-3 text-right', smallTextSize, semiBoldText, secondaryText)}>{t('waste.byProduct.colEntries')}</th>
              <th className={cn('px-4 py-3', smallTextSize, semiBoldText, secondaryText)}>{t('waste.byProduct.colSplit')}</th>
              <th className={cn('px-4 py-3', smallTextSize, semiBoldText, secondaryText)}>{t('waste.byProduct.colReasons')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.key} className={cn('border-b last:border-b-0', borderPrimary)}>
                <td className="px-4 py-3">
                  <span className={cn('block', smallTextSize, semiBoldText, primaryText)}>{row.productName}</span>
                  <span className={cn('block text-[11px]', tertiaryText)}>
                    {[row.platform, row.articleId].filter(Boolean).join(' · ')}
                  </span>
                </td>
                <td className={cn('px-4 py-3 text-right whitespace-nowrap', smallTextSize, semiBoldText, primaryText)}>
                  {fmtBase(row.totalQuantity, row.stdUnit)}
                  {row.mismatchedEntries > 0 && (
                    <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                      {t('waste.byProduct.mismatchBadge', { n: row.mismatchedEntries })}
                    </span>
                  )}
                </td>
                <td className={cn('px-4 py-3 text-right', smallTextSize, secondaryText)}>{row.entryCount}</td>
                <td className={cn('px-4 py-3 text-[11px]', tertiaryText)}>
                  {row.fromDishQuantity > 0 && (
                    <span className="block">
                      {t('waste.byProduct.fromDish', {
                        qty: fmtBase(row.fromDishQuantity, row.stdUnit),
                        n: row.fromDishEntries,
                      })}
                    </span>
                  )}
                  {row.fromIngredientQuantity > 0 && (
                    <span className="block">
                      {t('waste.byProduct.fromIngredient', {
                        qty: fmtBase(row.fromIngredientQuantity, row.stdUnit),
                        n: row.fromIngredientEntries,
                      })}
                    </span>
                  )}
                </td>
                <td className={cn('px-4 py-3 text-[11px]', tertiaryText)}>{reasonsLabel(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        }
      />
    </div>
  );
}
